import { on as onEvent } from "common/utils/ui/event";
import { scrollToElement, scrollToTop } from "common/utils/ui/scroll";

import "./product-details-navigation.scss";

const $getNavigationContainer = () => document.getElementById("js-pdp-navigation");
const $getColorsSlider = () => document.querySelector(".js-product-colors-slider");
const $getBackToTopLink = () => document.getElementById("js-pdp-back-top-link");
const $getProductDetailsBlock = () => document.getElementById("js-product-details-right-info");
const $getQuestionsBlock = () => document.getElementById("comments");
const $getReviewsBlock = () => document.getElementById("reviews-summary");
const $getPdpNavbar = () => document.getElementById("js-pdp-navbar");
const $getProductDetailsButton = () => document.getElementById("js-pdp-scroll-product-details");
const $getQuestionsButton = () => document.getElementById("js-pdp-scroll-questions");
const $getReviewsButton = () => document.getElementById("js-pdp-scroll-reviews");
const $getNavigationButtons = () => document.querySelectorAll(".js-pdp-navigation-scroll-button");

const getIntersectionRatio = observerEntry =>
  observerEntry.isIntersecting ? observerEntry.intersectionRatio : 0;

const isElementVisible = observerEntry => getIntersectionRatio(observerEntry) >= 0.25;
const isElementFullyVisible = observerEntry => getIntersectionRatio(observerEntry) >= 1;

const setNavigationElementVisibility = (targetElement, navElement) => {
  if (!navElement) return;

  // if element is fully visible or above the current screen - show element
  if (
    isElementVisible(targetElement) ||
    (!targetElement.isIntersecting && targetElement.boundingClientRect.top < 0)
  ) {
    navElement.classList.remove("hidden");
    return;
  }

  if (!targetElement.isIntersecting && targetElement.boundingClientRect.top > 0) {
    navElement.classList.add("hidden");
  }
};

class PDPNavigation {
  constructor($container) {
    this.$container = $container;
    this.$pdpNavbar = $getPdpNavbar();
    this.$productDetailsBlock = $getProductDetailsBlock();
    this.$colorsSlider = $getColorsSlider();
    this.$questionsBlock = $getQuestionsBlock();
    this.$reviewsBlock = $getReviewsBlock();
    this.$backToTopButton = $getBackToTopLink();
    this.$productDetailsButton = $getProductDetailsButton();
    this.$questionsButton = $getQuestionsButton();
    this.$reviewsButton = $getReviewsButton();
    this.$navigationButtons = $getNavigationButtons();
    this.reviewsLastIntersection = 0;
    this.questionsLastIntersection = 0;

    this.observer = new IntersectionObserver(this.navigationVisibilityToggler, {
      threshold: [0.25, 0.5, 0.75, 1.0]
    });
    this.initBackToTopButton();
    this.initProductDetailsButton();
    this.initQuestionsButton();
    this.initReviewsButton();
  }

  toggleQuestionsBlockVisibility = entry => {
    this.$questionsButton.classList.toggle(
      "product-details-navigation__navbar-button--active",
      isElementFullyVisible(entry) || (entry.isIntersecting && entry.intersectionRect.height >= 100)
    );

    if (this.reviewsLastIntersection > 0.5) {
      this.$reviewsButton.classList.add("product-details-navigation__navbar-button--active");
    }

    this.questionsLastIntersection = getIntersectionRatio(entry);
  };

  toggleReviewsBlockVisibility = entry => {
    this.$reviewsButton.classList.toggle(
      "product-details-navigation__navbar-button--active",
      (entry.isIntersecting && this.questionsLastIntersection < 1) ||
        (!entry.isIntersecting && entry.boundingClientRect.top < 0)
    );

    if (entry.isIntersecting && this.questionsLastIntersection < 1) {
      this.$questionsButton.classList.remove("product-details-navigation__navbar-button--active");
    }

    this.reviewsLastIntersection = getIntersectionRatio(entry);
  };

  navigationVisibilityToggler = entries => {
    entries.forEach(entry => {
      if (entry.target === this.$colorsSlider) {
        setNavigationElementVisibility(entry, this.$backToTopButton);
      }

      if (entry.target === this.$productDetailsBlock) {
        setNavigationElementVisibility(entry, this.$pdpNavbar);
        this.$productDetailsButton.classList.toggle(
          "product-details-navigation__navbar-button--active",
          entry.isIntersecting
        );
      }

      // custom logic for questions block because empty questions block overlaps with reviews on viewport
      // show questions button active only if it visible more than reviews block
      if (entry.target === this.$questionsBlock) {
        this.toggleQuestionsBlockVisibility(entry);
      }

      if (entry.target === this.$reviewsBlock) {
        this.toggleReviewsBlockVisibility(entry);
      }
    });
  };

  setActiveButton = currentButton => {
    this.$navigationButtons.forEach(btn =>
      btn.classList.toggle(
        "product-details-navigation__navbar-button--active",
        btn === currentButton
      )
    );
  };

  initBackToTopButton = () => {
    if (!this.$colorsSlider) return;

    this.observer.observe(this.$colorsSlider);
    onEvent("click", ".js-pdp-scroll-top", () => {
      scrollToTop();
      this.$pdpNavbar.classList.add("hidden");
    });
  };

  initProductDetailsButton = () => {
    if (!this.$productDetailsBlock) return;

    this.observer.observe(this.$productDetailsBlock);
    onEvent("click", "#js-pdp-scroll-product-details", () => {
      this.setActiveButton(this.$productDetailsButton);
      scrollToElement(this.$productDetailsBlock, true);
    });
  };

  initQuestionsButton = () => {
    if (!this.$questionsBlock) return;

    this.observer.observe(this.$questionsBlock);

    onEvent("click", "#js-pdp-scroll-questions", () => {
      this.setActiveButton(this.$questionsButton);
      scrollToElement(this.$questionsBlock, true);
    });
  };

  initReviewsButton = () => {
    if (!this.$reviewsBlock) return;

    this.observer.observe(this.$reviewsBlock);

    onEvent("click", "#js-pdp-scroll-reviews", () => {
      this.setActiveButton(this.$reviewsButton);
      scrollToElement(this.$reviewsBlock, true);
    });
  };
}

const initPdpNavigation = () => {
  const $container = $getNavigationContainer();
  if (!$container) return null;
  return new PDPNavigation($container);
};

export default initPdpNavigation;
